/**
 * PATH: apps/qa/src/pages/NewInspectionPage.tsx
 * PURPOSE: Create new QA inspections — wizard orchestrator
 * WHY: Subcontractors need a guided workflow for NCC-compliant inspections
 *
 * FLOW:
 *   ┌─────────────┐    ┌──────────────┐    ┌─────────────┐    ┌─────────────┐
 *   │ Select Job  │───▶│ INLINE Form  │───▶│ Add Floors  │───▶│ Review &    │
 *   │ or Manual   │    │ + AI Badges  │    │ & Areas     │    │ Create      │
 *   └─────────────┘    └──────────────┘    └─────────────┘    └─────────────┘
 */

import { useMemo, useState } from 'react'
import { ArrowLeft, ArrowRight, Check, Loader2, AlertTriangle, Sparkles, BookTemplate, X } from 'lucide-react'
import { getConfiguredClient } from '@quotech/api'
import { createDjangoJobHubClient } from '@quotech/job-hub/jobs/djangoJobHubClient'
import { PhoneInspectionSetup } from '@quotech/job-hub/jobs/qa/phoneInspectionSetup'
import { cn } from '@/lib/utils'
import { useQaAppCapabilities } from '@/lib/useQaAppCapabilities'
import { CATEGORY_INFO, QUICK_TEMPLATES } from '@/lib/inspection-constants'

import { STEPS } from './new-inspection/inspection-types'
import QuoteSourceStep from './new-inspection/QuoteSourceStep'
import { useNewInspection } from './new-inspection/useNewInspection'
import { Toast } from './new-inspection/Toast'
import JobSelectStep from './new-inspection/JobSelectStep'
import NewJobDetailsPopup from './new-inspection/NewJobDetailsPopup'
import DetailsStep from './new-inspection/DetailsStep'
import FloorsStep from './new-inspection/FloorsStep'
import ReviewStep from './new-inspection/ReviewStep'
import SidePanel from './new-inspection/SidePanel'

export default function NewInspectionPage() {
  const [showArchivedCards, setShowArchivedCards] = useState(false)
  const ctx = useNewInspection()
  const {
    navigate, currentStep, currentStepId, showTemplates, setShowTemplates,
    selectedJob, setSelectedJob, jobSearch, setJobSearch, jobs, jobsLoading,
    inspectionName, setInspectionName, category, setCategory, projectType, setProjectType,
    buildingClasses, setBuildingClasses, tradeTags, setTradeTags: _setTradeTags,
    customTradeTags, newCustomTag, setNewCustomTag, customTagInputRef,
    publishAfterCreate, setPublishAfterCreate, saveAsTemplate, setSaveAsTemplate,
    aiSuggestedCategory, aiSuggestedClass, aiSuggestedTrades, aiConfidence, aiReasoning,
    hasManualClassSelection: _hasManualClassSelection, setHasManualClassSelection, suggestedClasses, suggestedTrades,
    floorMgr, quoteSource, toasts, showToast, dismissToast, createMutation,
    validationWarnings, completionProgress,
    canProceed, goNext, goBack, goToStep, goToStepId,
    toggleTradeTag, addCustomTag, removeCustomTag, handleSelectJob, applyQuickTemplate,
    newJobOpen, setNewJobOpen, handleJobCreated, organisationId,
    hasNextJobs, jobsFetchingNext, fetchNextJobs,
  } = ctx
  const { canCreateQaJob } = useQaAppCapabilities()
  const numericOrgId = organisationId != null ? Number(organisationId) : undefined
  const hubClient = useMemo(
    () => createDjangoJobHubClient(getConfiguredClient(), { orgId: numericOrgId }),
    [numericOrgId],
  )

  if (!canCreateQaJob) {
    return (
      <div className="max-w-5xl mx-auto space-y-6">
        <h1 className="text-2xl font-bold text-foreground">New Inspection</h1>
        <p className="text-muted-foreground">Only QA builders can create a QA.</p>
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/records')} className="p-2 rounded-lg hover:bg-accent transition-colors" aria-label="Go back to records">
          <ArrowLeft className="w-5 h-5 text-muted-foreground" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-foreground">New Inspection</h1>
          <p className="text-muted-foreground">Create a QA inspection record for compliance documentation</p>
        </div>
        <button onClick={() => setShowTemplates(!showTemplates)}
          className={cn('inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors',
            showTemplates ? 'border-primary bg-primary/10 text-primary' : 'border-input hover:bg-accent')}>
          <BookTemplate className="w-4 h-4" />Templates
        </button>
      </div>

      {/* Quick Templates Panel */}
      {showTemplates && (
        <div className="bg-card rounded-xl shadow-sm border border-border p-4 animate-in slide-in-from-top-2 duration-200">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-500" /><h3 className="font-medium text-foreground">Quick Start Templates</h3></div>
            <button onClick={() => setShowTemplates(false)} className="p-1 hover:bg-accent rounded"><X className="w-4 h-4 text-muted-foreground" /></button>
          </div>
          <p className="text-sm text-muted-foreground mb-4">Start with a pre-configured template to speed up inspection creation. All settings can be customized after.</p>
          {!showArchivedCards ? (
            <button type="button" className="text-sm font-medium text-primary hover:underline" onClick={() => setShowArchivedCards(true)}>
              Archived templates
            </button>
          ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {QUICK_TEMPLATES.map(template => {
              const CategoryIcon = CATEGORY_INFO[template.category].icon
              return (
                <button key={template.id} onClick={() => applyQuickTemplate(template)}
                  className="p-4 rounded-lg border border-border hover:border-primary hover:bg-primary/5 text-left transition-all group">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                      <CategoryIcon className="w-4 h-4 text-muted-foreground group-hover:text-primary" />
                    </div>
                    <span className="font-medium text-sm text-foreground">{template.name}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">{template.buildingClasses.join(', ')}</p>
                  <div className="flex flex-wrap gap-1">
                    {template.trades.slice(0, 3).map(trade => <span key={trade} className="text-[10px] px-1.5 py-0.5 bg-muted rounded text-muted-foreground">{trade}</span>)}
                    {template.trades.length > 3 && <span className="text-[10px] px-1.5 py-0.5 bg-muted rounded text-muted-foreground">+{template.trades.length - 3}</span>}
                  </div>
                </button>
              )
            })}
          </div>
          )}
        </div>
      )}

      {/* Progress Steps */}
      <div className="flex items-center justify-between">
        {STEPS.filter((step) => step.id !== 'details').map((step) => {
          const idx = STEPS.findIndex((row) => row.id === step.id)
          const Icon = step.icon; const isActive = idx === currentStep; const isCompleted = idx < currentStep
          return (
            <div key={step.id} className="flex items-center flex-1">
              <button onClick={() => goToStep(idx)} disabled={idx > currentStep}
                className={cn('flex items-center gap-2 transition-colors', idx < currentStep && 'cursor-pointer hover:opacity-80')}>
                <div className={cn('w-10 h-10 rounded-full flex items-center justify-center transition-all',
                  isActive && 'bg-primary text-primary-foreground shadow-md shadow-primary/25',
                  isCompleted && 'bg-emerald-500 text-white',
                  !isActive && !isCompleted && 'bg-muted text-muted-foreground')}>
                  {isCompleted ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                </div>
                <span className={cn('text-sm font-medium hidden sm:block transition-colors', isActive ? 'text-foreground' : 'text-muted-foreground')}>{step.label}</span>
              </button>
              {step.id !== 'review' && <div className={cn('flex-1 h-0.5 mx-4 transition-colors', isCompleted ? 'bg-emerald-500' : 'bg-muted')} />}
            </div>
          )
        })}
      </div>

      {/* Main Content Area */}
      <div className={cn('gap-6', currentStepId !== 'job' ? 'grid grid-cols-1 lg:grid-cols-3' : '')}>
        <div className={cn('bg-card rounded-xl shadow-sm border border-border p-6', currentStepId !== 'job' ? 'lg:col-span-2' : '')}>
          {currentStepId === 'job' && (
            <JobSelectStep selectedJob={selectedJob} jobSearch={jobSearch} setJobSearch={setJobSearch}
              jobs={jobs} jobsLoading={jobsLoading} onSelectJob={handleSelectJob} onClearJob={() => setSelectedJob(null)}
              onNewJob={canCreateQaJob ? () => setNewJobOpen(true) : undefined}
              hasNextPage={hasNextJobs}
              fetchingNext={jobsFetchingNext}
              onLoadMore={() => { void fetchNextJobs() }} />
          )}
          {currentStepId === 'source' && (
            <>
              <div className="md:hidden">
                <PhoneInspectionSetup
                  jobId={Number(selectedJob?.job_id || 0)}
                  orgId={numericOrgId}
                  client={hubClient}
                  classification={{ category, projectType, buildingClasses, tradeTags }}
                  quotes={<QuoteSourceStep source={quoteSource} />}
                  onCreated={(id) => navigate(`/records/${id}`)}
                />
              </div>
              <div className="hidden md:block">
                <QuoteSourceStep source={quoteSource} />
              </div>
            </>
          )}
          {currentStepId === 'details' && (
            <DetailsStep selectedJob={selectedJob} inspectionName={inspectionName} setInspectionName={setInspectionName}
              category={category} setCategory={setCategory} projectType={projectType} setProjectType={setProjectType}
              buildingClasses={buildingClasses} setBuildingClasses={setBuildingClasses}
              tradeTags={tradeTags} suggestedClasses={suggestedClasses} suggestedTrades={suggestedTrades}
              customTradeTags={customTradeTags} newCustomTag={newCustomTag} setNewCustomTag={setNewCustomTag}
              customTagInputRef={customTagInputRef}
              aiSuggestedCategory={aiSuggestedCategory} aiSuggestedClass={aiSuggestedClass}
              aiSuggestedTrades={aiSuggestedTrades} aiConfidence={aiConfidence} aiReasoning={aiReasoning}
              setHasManualClassSelection={setHasManualClassSelection}
              toggleTradeTag={toggleTradeTag} addCustomTag={addCustomTag} removeCustomTag={removeCustomTag} />
          )}
          {currentStepId === 'structure' && (
            <FloorsStep selectedJob={selectedJob} buildingClasses={buildingClasses} tradeTags={tradeTags}
              category={category} projectType={projectType}
              floors={floorMgr.floors} totalAreas={floorMgr.totalAreas}
              addFloor={floorMgr.addFloor} removeFloor={floorMgr.removeFloor}
              updateFloor={floorMgr.updateFloor} updateFloorNotes={floorMgr.updateFloorNotes}
              addArea={floorMgr.addArea} addBulkAreas={floorMgr.addBulkAreas}
              removeArea={floorMgr.removeArea} updateArea={floorMgr.updateArea}
              toggleAreaExpanded={floorMgr.toggleAreaExpanded}
              addAreaAttachments={floorMgr.addAreaAttachments} removeAreaAttachment={floorMgr.removeAreaAttachment}
              applyGeneratedFloors={floorMgr.applyGeneratedFloors} showToast={showToast} />
          )}
          {currentStepId === 'review' && (
            <ReviewStep selectedJob={selectedJob} inspectionName={inspectionName}
              category={category} projectType={projectType}
              buildingClasses={buildingClasses} tradeTags={tradeTags}
              floors={floorMgr.floors} totalAreas={floorMgr.totalAreas}
              validationWarnings={validationWarnings}
              publishAfterCreate={publishAfterCreate} setPublishAfterCreate={setPublishAfterCreate}
              saveAsTemplate={saveAsTemplate} setSaveAsTemplate={setSaveAsTemplate}
              goToStep={goToStep} goToStepId={goToStepId} />
          )}
        </div>

        {/* Side Panel */}
        {currentStepId !== 'job' && (
          <SidePanel selectedJob={selectedJob} inspectionName={inspectionName}
            category={category} projectType={projectType}
            buildingClasses={buildingClasses} tradeTags={tradeTags}
            floors={floorMgr.floors} totalAreas={floorMgr.totalAreas}
            completionProgress={completionProgress} />
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button onClick={goBack} disabled={currentStepId === 'job'}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-input text-foreground font-medium hover:bg-accent active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
          <ArrowLeft className="w-4 h-4" />Back
        </button>
        {currentStep < STEPS.length - 1 ? (
          <button onClick={goNext} disabled={!canProceed()}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-primary-foreground font-medium hover:bg-primary/90 active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm">
            Next<ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <button onClick={() => createMutation.mutate()} disabled={createMutation.isPending || !canProceed()}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-primary-foreground font-medium hover:bg-primary/90 active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm">
            {createMutation.isPending ? <><Loader2 className="w-4 h-4 animate-spin" />Creating...</> : <><Check className="w-4 h-4" />Create Inspection</>}
          </button>
        )}
      </div>

      {canCreateQaJob && newJobOpen && organisationId != null && (
        <NewJobDetailsPopup
          organisationId={organisationId}
          onClose={() => setNewJobOpen(false)}
          onCreated={handleJobCreated}
          showToast={showToast}
        />
      )}

      {/* Toast Notifications */}
      {toasts.length > 0 && (
        <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 max-w-sm" role="region" aria-label="Notifications">
          {toasts.map(toast => <Toast key={toast.id} notification={toast} onDismiss={dismissToast} />)}
        </div>
      )}

      {/* Error display */}
      {createMutation.isError && (
        <div className="p-4 rounded-xl bg-destructive/10 border border-destructive/20 flex items-start gap-3 animate-in slide-in-from-bottom-2">
          <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-destructive">Failed to create inspection</p>
            <p className="text-sm text-destructive/80 mt-1">{createMutation.error instanceof Error ? createMutation.error.message : 'Please try again'}</p>
          </div>
        </div>
      )}
    </div>
  )
}
